import * as THREE from "three";
import type { GroundVehicle } from "./groundVehicle";

/**
 * Chase camera for ground vehicles, with two jobs beyond following the chassis.
 *
 * SCROLL-IN SHOULDER VIEW. The wheel dollies the boom from the wide chase frame
 * all the way into an over-the-shoulder view — the same frame right-click gives
 * on foot: close behind the rider and shifted off the centreline so the vehicle
 * does not eat the middle of the screen. The lateral shift matches the on-foot
 * aim camera exactly (0.55 m) so the two read as one idea.
 *
 * SPEED CUES. A vehicle at 60 m/s over open terrain reads as barely moving
 * unless the camera says otherwise, so the standard racing-camera kit is applied
 * here, all of it scaled off `speedK`:
 *   - FOV widens with speed and PUNCHES on boost before settling. Field of view
 *     is the strongest single speed cue — narrow reads slow, wide reads fast.
 *   - the camera sinks toward the deck as speed rises. Ground-plane parallax is
 *     what the eye actually measures speed against, and dropping the eye
 *     multiplies it.
 *   - the boom lengthens with speed, opening the frame where it matters.
 *   - the camera LAGS under acceleration and noses in under braking — the cue
 *     that separates "fast" from "getting faster".
 *   - a small vertical tremble above half speed: perfectly steady motion reads
 *     as static, and breaking it up reads as speed.
 *   - the camera rolls slightly into turns.
 * Every amplitude is deliberately small. The consistent advice on these effects
 * is that overdoing any one reads as nausea rather than speed, so each is a
 * nudge and the combination carries the feel.
 */

/** Over-the-shoulder frame, reached by scrolling all the way in. The boom is
 * measured from the chassis ORIGIN, so a fixed distance puts the camera inside
 * the tail of anything longer than a bike — it scales off the collider instead. */
const SHOULDER_CLEARANCE = 2.7;
/** Just above the deck. Sitting the camera low and aiming a touch ABOVE it tilts
 * the view up and lifts the vehicle off the bottom edge into the middle of the
 * frame — the geometry is worth stating because it is easy to get backwards:
 * raising the aim point pushes the subject DOWN the screen, so a low camera has
 * to aim only slightly above itself, not at the horizon. */
const SHOULDER_HEIGHT = 0.35;
/** Height above the chassis the shoulder frame aims at (chase aims at 0.8). */
const SHOULDER_LOOK_HEIGHT = 0.35;
/** Same off-centre shift the on-foot aim camera uses. */
const SHOULDER_SHIFT = 0.55;
const SHOULDER_LOOKAHEAD = 9;
/** Fraction of the full chase↔shoulder range one wheel notch covers. */
const ZOOM_STEP = 0.18;

/** Metres the camera sinks toward the deck at top speed. */
const SPEED_DROP = 1.7;
/** Speed cues ramp against top speed INCLUDING the boost overdrive, on a
 * smoothstep — so they build gradually across the whole usable range instead of
 * saturating the moment you reach cruising speed. */
const SPEED_CURVE = (k: number): number => k * k * (3 - 2 * k);
/** Extra boom length at top speed. */
const SPEED_PULLBACK = 4;
/** Metres of trail per m/s² of acceleration, and its ceiling. */
const LAG_PER_ACCEL = 0.055;
const LAG_MAX = 1.7;
/** Vertical tremble amplitude at top speed. */
/** Deliberately near-zero: the tremble read as the ground shaking rather than as
 * speed, so it is dialled to about 1% of where it started. Set to 0 to remove it
 * entirely — nothing else depends on it. */
const TREMBLE_AMP = 0.0005;
/** Radians of camera roll into a sustained turn — deliberately tiny. */
const BANK_MAX = 0.018;
/** Turn rate (rad/s) that must be EXCEEDED before the camera rolls at all, so
 * ordinary steering corrections never tilt the horizon. */
const BANK_DEADZONE = 0.5;
/** Degrees of FOV added by the boost punch, and how fast it bleeds off. */
const BOOST_PUNCH = 9;
const PUNCH_DECAY = 4.5;

export class VehicleCamera {
  /** 0 = wide chase, 1 = over-the-shoulder. Driven by the wheel. */
  private zoomTarget = 0;
  private zoomK = 0;
  private target = new THREE.Vector3();
  private lookTarget = new THREE.Vector3();
  private offset = new THREE.Vector3();
  private smoothOffset = new THREE.Vector3();
  private shakeRight = new THREE.Vector3();
  private right = new THREE.Vector3();
  private viewDir = new THREE.Vector3();
  private prevHeading = new THREE.Vector3();
  private shake = 0;
  private shakePhase = 0;
  private lastSpeed = 0;
  private smoothAccel = 0;
  private punch = 0;
  private turnRate = 0;
  private warpK = 0;
  private ready = false;

  snap(): void { this.ready = false; }

  impact(speed: number): void {
    this.shake = Math.max(this.shake, Math.min(1, Math.max(0, speed - 4) / 34));
  }

  /** Wheel input: scrolling "in" (negative deltaY) pulls toward the shoulder. */
  zoomBy(deltaY: number): void {
    if (deltaY === 0) return;
    this.zoomTarget = Math.max(0, Math.min(1, this.zoomTarget - Math.sign(deltaY) * ZOOM_STEP));
  }

  /** 0 = wide chase, 1 = over-the-shoulder. */
  get shoulderAmount(): number { return this.zoomK; }

  /** How hard the environment should warp, 0..1 — see updateSpeedWarp. Only the
   * top of the speed range contributes, so ordinary driving is undistorted. */
  get warpAmount(): number { return this.warpK; }

  /** Called when boost engages, for the FOV kick. */
  boostEngaged(): void { this.punch = BOOST_PUNCH; }

  update(camera: THREE.PerspectiveCamera, vehicle: GroundVehicle, renderPos: THREE.Vector3, origin: THREE.Vector3, dt: number): void {
    const d = vehicle.def.camera;
    const speed = Math.abs(vehicle.forwardSpeed);
    // A flyer's handling.maxSpeed is its taxi crawl; normalizing by it pinned
    // speedK at 1 for the whole flight and ran every speed-scaled effect —
    // pullback, drop, look-ahead, and the environment warp — at maximum from
    // the moment it cruised. The flight block's authored cruiseSpeed is the
    // real top of a flyer's range.
    const topReference = Math.max(
      vehicle.def.handling.maxSpeed * (vehicle.def.boost?.speedMultiplier ?? 1),
      vehicle.def.flight?.cruiseSpeed ?? 0,
    );
    const speedK = SPEED_CURVE(Math.min(1, speed / topReference));
    this.zoomK += (this.zoomTarget - this.zoomK) * (1 - Math.exp(-6 * dt));

    // Acceleration trails the camera, braking noses it in. Smoothed hard —
    // raw frame-to-frame acceleration is far too noisy to drive a camera with.
    const accel = (speed - this.lastSpeed) / Math.max(dt, 1e-4);
    this.lastSpeed = speed;
    this.smoothAccel += (accel - this.smoothAccel) * (1 - Math.exp(-6 * dt));
    const lag = Math.max(-LAG_MAX, Math.min(LAG_MAX, this.smoothAccel * LAG_PER_ACCEL));

    // Signed turn rate about the vehicle's up, for the roll-into-the-turn cue.
    this.right.crossVectors(vehicle.viewHeading, vehicle.up).normalize();
    if (this.ready) {
      const swing = this.prevHeading.dot(this.right) / Math.max(dt, 1e-4);
      // Very slow: the roll should answer a sustained turn, not a flick of the
      // mouse. This easing is what "more movement needed before it happens"
      // actually means — the rate has to be held to build up.
      this.turnRate += (swing - this.turnRate) * (1 - Math.exp(-1.1 * dt));
    }
    this.prevHeading.copy(vehicle.viewHeading);

    // Boom: authored distance, opened up by speed, then dollied toward the
    // shoulder frame by the wheel.
    const shoulderDistance = vehicle.def.collider.radius + SHOULDER_CLEARANCE;
    const chaseDistance = d.distance + speedK * SPEED_PULLBACK;
    const distance = chaseDistance + (shoulderDistance - chaseDistance) * this.zoomK;
    const height = d.height + (SHOULDER_HEIGHT - d.height) * this.zoomK;
    const cp = Math.cos(vehicle.cameraPitch);
    const sp = Math.sin(vehicle.cameraPitch);
    this.offset.copy(vehicle.viewHeading).multiplyScalar(-cp * distance - lag);
    // The speed pull-back extends the boom BACKWARD only: the pitch term uses the
    // un-extended distance, so lengthening the boom cannot raise the camera. Get
    // this wrong and the drop cue measures backwards — a longer boom at a fixed
    // pitch lifts the camera by more than the drop lowers it.
    const pitchDistance = d.distance + (shoulderDistance - d.distance) * this.zoomK;
    this.offset.addScaledVector(vehicle.up, height + sp * pitchDistance - speedK * SPEED_DROP * (1 - this.zoomK));
    this.offset.addScaledVector(this.right, SHOULDER_SHIFT * this.zoomK);

    const lookAhead = d.lookAhead + speedK * 10;
    this.target.copy(renderPos)
      .addScaledVector(vehicle.up, 0.8 + (SHOULDER_LOOK_HEIGHT - 0.8) * this.zoomK)
      .addScaledVector(this.right, SHOULDER_SHIFT * this.zoomK)
      .addScaledVector(vehicle.aimHeading, lookAhead + (SHOULDER_LOOKAHEAD - lookAhead) * this.zoomK);

    if (!this.ready) { this.smoothOffset.copy(this.offset); this.ready = true; }
    // Slow follow. The camera used to whip round with the chassis, which made
    // every steering input read as a jolt; trailing the turn and easing back is
    // what makes speed feel smooth rather than twitchy.
    else this.smoothOffset.lerp(this.offset, 1 - Math.exp(-3.5 * dt));

    this.shakePhase += dt * 47;
    this.shake *= Math.exp(-8.5 * dt);
    camera.position.copy(renderPos).add(this.smoothOffset).sub(origin);

    // Impact shake (collisions), then the speed tremble. The tremble only starts
    // above half speed, and is damped in the shoulder frame where the camera sits
    // close enough that the same amplitude would read as a rattle.
    if (this.shake > 0.002) {
      this.shakeRight.crossVectors(vehicle.viewHeading, vehicle.up).normalize();
      const amp = this.shake * 0.34;
      camera.position.addScaledVector(this.shakeRight, Math.sin(this.shakePhase * 1.31) * amp);
      camera.position.addScaledVector(vehicle.up, Math.sin(this.shakePhase * 1.83 + 0.7) * amp * 0.65);
    }
    const trembleK = Math.max(0, speedK - 0.5) * 2 + vehicle.boostK * 0.5;
    if (trembleK > 0.01) {
      const amp = TREMBLE_AMP * trembleK * (1 - 0.55 * this.zoomK);
      camera.position.addScaledVector(vehicle.up, Math.sin(this.shakePhase * 2.7) * amp);
      camera.position.addScaledVector(this.right, Math.sin(this.shakePhase * 1.9 + 1.3) * amp * 0.4);
    }

    // Roll into the turn: the up vector is rolled about the view direction, so
    // the horizon tilts without the camera leaving its boom.
    this.lookTarget.copy(this.target).sub(origin);
    camera.up.copy(vehicle.up);
    const banked = Math.sign(this.turnRate) * Math.max(0, Math.abs(this.turnRate) - BANK_DEADZONE);
    const bank = Math.max(-BANK_MAX, Math.min(BANK_MAX, -banked * 0.18)) * (1 - 0.4 * this.zoomK);
    if (Math.abs(bank) > 1e-4) {
      this.viewDir.copy(this.lookTarget).sub(camera.position).normalize();
      camera.up.applyAxisAngle(this.viewDir, bank);
    }
    camera.lookAt(this.lookTarget);

    // Environment warp: VERY high speed only. Boost must not contribute on its
    // own — as a flat additive term it fired the moment you tapped boost at any
    // speed, which is exactly what "it triggers when going too slowly" was. It
    // is a multiplier on an already-fast state instead, and the whole thing is
    // squared so it ramps in late rather than creeping up through the midrange.
    const fast = Math.max(0, (speedK - 0.72) / 0.28);
    // The warp is a GROUND RUSH cue, so it also fades with clearance: full
    // effect skimming the deck, gone by ~40 m up. Without this, a cruising
    // flyer bent the whole horizon around itself and dragged the sea into the
    // frame edges — the "world curves around the player" report.
    const groundRush = THREE.MathUtils.clamp(1 - (vehicle.groundClearance - 8) / 32, 0, 1);
    const warpTarget = Math.min(1, fast * fast * (0.9 + 0.4 * vehicle.boostK)) * groundRush;
    this.warpK += (warpTarget - this.warpK) * (1 - Math.exp(-2.5 * dt));

    // FOV: speed widens it, boost punches it. The punch is fed by boost ENGAGING
    // rather than by its level, so it hits on the press and settles while held.
    this.punch = Math.max(0, this.punch * Math.exp(-PUNCH_DECAY * dt));
    const targetFov = d.baseFov + d.speedFov * speedK + vehicle.boostK * 5 + this.punch;
    camera.fov += (targetFov - camera.fov) * (1 - Math.exp(-2.2 * dt));
    camera.updateProjectionMatrix();
  }
}
